import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner with Image */}
      <div 
        className="relative bg-gradient-to-r from-brand-800 to-brand-600 py-32 sm:py-40 overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1764996915324-91919cee14d3')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">About Us – 99prop.com</h1>
          <p className="text-2xl sm:text-3xl text-white font-semibold">
            Real Estate. Reimagined with Intelligence & Transparency.
          </p>
        </div>
      </div>

      {/* Section 1: Introduction */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 lg:p-16">
          <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed mb-6 font-semibold">
            At 99prop.com, we are not just another property portal.
          </p>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
            We are building a smarter, more transparent, and data-driven real estate ecosystem where buyers and sellers have complete control over their decisions — not brokers, not speculation, not inflated pricing.
          </p>
        </div>
      </div>

      {/* Section 2: Our Vision */}
      <div className="bg-gradient-to-br from-brand-50 to-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Our Vision</h2>
              <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                <p>
                  The Indian real estate market has long been unstructured, emotionally driven, and often opaque. Prices fluctuate without logic. Negotiations lack transparency. Buyers overpay. Sellers undersell.
                </p>
                <p className="font-semibold text-brand-500 text-xl">
                  We are here to change that.
                </p>
                <p>
                  Our vision is to bring structure, evaluation-based pricing, and professional deal execution into the property market — just like modern financial markets.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1745429523617-0d837856ca35"
                alt="Modern Indian Home Interior"
                className="rounded-3xl shadow-2xl w-full h-80 sm:h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: What We Do Differently */}
      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-4">
            What We Do Differently
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 text-center mb-12">
            Unlike traditional property platforms that only list properties, 99prop.com focuses on:
          </p>

          {/* 1. Smart Property Evaluation */}
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-brand-500 text-white rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold">
                1
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Smart Property Evaluation</h3>
            </div>
            
            <p className="text-gray-700 text-lg mb-6">
              We use structured property evaluation models that consider:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Location score</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Floor & facing adjustments</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Condition & renovation cost</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Market comparables</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Urgency factors</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">✓</span>
                <span className="text-gray-700">Profitability & resale potential</span>
              </div>
            </div>

            <div className="bg-brand-50 border-l-4 border-brand-500 p-6 rounded-r-lg">
              <p className="text-lg font-semibold text-gray-900">Our goal is simple:</p>
              <p className="text-gray-700 text-lg">Help you know the real value of a property — before you invest.</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* 2. Transparent Process */}
            <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-3xl shadow-xl p-8 sm:p-10 text-white">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-white text-brand-500 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold">
                  2
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold">Transparent Buying & Selling Process</h3>
              </div>
              
              <p className="text-brand-100 text-lg mb-4 font-semibold">We believe:</p>
              <ul className="space-y-3 mb-6 text-brand-50">
                <li>• No hidden pricing manipulation</li>
                <li>• No last-minute deal changes</li>
                <li>• No unclear commission structures</li>
              </ul>

              <p className="text-brand-100 text-lg mb-4 font-semibold">Every deal is:</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">✔</span>
                  <span className="text-lg">Structured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">✔</span>
                  <span className="text-lg">Documented</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">✔</span>
                  <span className="text-lg">Consent-based</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">✔</span>
                  <span className="text-lg">Transparent</span>
                </div>
              </div>

              <p className="text-white text-lg mt-6 font-semibold">
                Buyers and sellers both stay informed at every step.
              </p>
            </div>

            {/* Image */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/c5nenr1z_Finalizing%20the%20deal%20with%20a%20handshake.png"
                alt="North Indian Real Estate Agent Finalizing Deal with Indian Couple"
                className="rounded-3xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>

          {/* 3. Speed with Clarity */}
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-brand-500 text-white rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold">
                3
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Speed with Clarity</h3>
            </div>
            
            <p className="text-gray-700 text-lg mb-6">
              Real estate transactions shouldn't take months of confusion.
            </p>

            <p className="text-gray-700 text-lg mb-4 font-semibold">We streamline:</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">→</span>
                <span className="text-gray-700">Due diligence checks</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">→</span>
                <span className="text-gray-700">Renovation planning estimates</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">→</span>
                <span className="text-gray-700">Market-based resale strategy</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-brand-500 text-xl">→</span>
                <span className="text-gray-700">Structured offer negotiations</span>
              </div>
            </div>

            <p className="text-gray-700 text-lg font-semibold">
              This ensures faster closures — without compromising safety.
            </p>
          </div>
        </div>
      </div>

      {/* Section 4: Our Mission */}
      <div className="bg-gradient-to-br from-brand-500 to-brand-700 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/9aa7bu1f_Balcony%20view%20of%20vibrant%20community%20playground.png"
                alt="Modern Indian Apartment Community with Playground"
                className="rounded-3xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Our Mission</h2>
              <p className="text-xl sm:text-2xl mb-6">Our mission is to:</p>
              <div className="space-y-4 text-lg sm:text-xl">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">✔</span>
                  <span>Bring data-backed pricing to Indian real estate</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">✔</span>
                  <span>Protect buyers from overpaying</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">✔</span>
                  <span>Help sellers get fair and justified value</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">✔</span>
                  <span>Introduce structured co-investment models</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">✔</span>
                  <span>Build a controlled, ethical, and scalable property ecosystem</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Why 99prop.com */}
      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12">
            Why 99prop.com?
          </h2>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <p className="text-xl sm:text-2xl text-gray-800 font-semibold mb-6">Because we believe:</p>
              <div className="space-y-4 text-lg text-gray-700">
                <p className="flex items-start space-x-3">
                  <span className="text-brand-500 text-2xl">•</span>
                  <span>Real estate should be <strong>evaluated</strong>, not guessed.</span>
                </p>
                <p className="flex items-start space-x-3">
                  <span className="text-brand-500 text-2xl">•</span>
                  <span>Deals should be <strong>structured</strong>, not manipulated.</span>
                </p>
                <p className="flex items-start space-x-3">
                  <span className="text-brand-500 text-2xl">•</span>
                  <span>Investors deserve <strong>clarity</strong>, not confusion.</span>
                </p>
                <p className="flex items-start space-x-3">
                  <span className="text-brand-500 text-2xl">•</span>
                  <span><strong>Transparency</strong> builds long-term trust.</span>
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/no0d3v5x_Family%20joy%20in%20a%20sunny%20living%20room.png"
                alt="Happy North Indian Family in Modern Home"
                className="rounded-3xl shadow-2xl w-full h-80 object-cover"
                style={{ objectPosition: '50% 35%' }}
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-brand-50 to-brand-100 rounded-3xl p-8 sm:p-12">
            <p className="text-xl sm:text-2xl text-gray-800 font-semibold mb-6">At 99prop.com, we combine:</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border-2 border-blue-200 hover:border-brand-400 transition">
                <div className="w-16 h-16 mx-auto mb-4 bg-brand-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Analytical thinking</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border-2 border-blue-200 hover:border-brand-400 transition">
                <div className="w-16 h-16 mx-auto mb-4 bg-brand-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Legal structure</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border-2 border-blue-200 hover:border-brand-400 transition">
                <div className="w-16 h-16 mx-auto mb-4 bg-brand-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Renovation planning logic</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border-2 border-blue-200 hover:border-brand-400 transition">
                <div className="w-16 h-16 mx-auto mb-4 bg-brand-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Business-level deal execution</p>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl text-center font-bold text-gray-900">
              To give you something rare in Indian real estate:
            </p>
            <p className="text-4xl sm:text-5xl text-center font-bold text-brand-500 mt-4">
              Confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Section 6: Our Commitment */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/qj7zx4xn_young%20real%20estate%20agent%2099prop.png"
                alt="Professional Real Estate Team"
                className="rounded-3xl shadow-2xl w-full h-96 lg:h-[500px] object-cover object-center"
              />
            </div>
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Our Commitment</h2>
              <p className="text-xl mb-6">When you work with us, you get:</p>
              <div className="space-y-4 text-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-brand-400 text-2xl">✓</span>
                  <span>Honest valuation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-brand-400 text-2xl">✓</span>
                  <span>Structured documentation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-brand-400 text-2xl">✓</span>
                  <span>Clear financial breakdown</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-brand-400 text-2xl">✓</span>
                  <span>Transparent communication</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-brand-400 text-2xl">✓</span>
                  <span>Accountability at every stage</span>
                </div>
              </div>
              <div className="mt-8 space-y-3 text-xl">
                <p>We focus on quality, clarity, and long-term relationships.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-16 sm:py-20 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">99prop.com</h2>
          <p className="text-2xl sm:text-3xl text-brand-500 font-bold mb-8">
            Smarter Deals. Structured Growth. Transparent Transactions.
          </p>
          <Link
            to="/"
            className="inline-block bg-brand-500 text-white px-12 py-5 rounded-xl font-bold text-xl hover:bg-brand-600 transition shadow-2xl hover:scale-105 transform"
          >
            Get Your Property Valued Now →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
